/**
 * @file TransferPicker.tsx
 * @description «Передать диалог» (chat-window-design.md §1д, §4, §7):
 * окно 400 на широком экране, нижняя шторка до 90% высоты на телефоне.
 * Сотрудники с правом переписки, кроме меня (`GET /admin/chat/staff`):
 * блок «Ведёт заказ», затем «Остальные»; в строке инициалы, имя, роль и
 * «N диалогов»; нынешний ответственный неактивен — «ведёт сейчас».
 */

import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ListError } from "../ui/ListStates";
import { SearchInput } from "../ui/SearchInput";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Skeleton } from "../ui/skeleton";
import { adminChat, type TransferCandidate } from "../../lib/api-chat";
import { chatErrorText } from "./chat-errors";
import { CHAT_TEXT } from "./chat-texts";
import { TRANSFER_SEARCH_FROM, transferGroups } from "./transfer-groups";
import { TransferRow } from "./TransferRow";

interface Props {
  open: boolean;
  conversationId: string;
  phone: boolean;
  busy: boolean;
  onClose: () => void;
  /** true — передали, окно закрывается. */
  onTransfer: (to: TransferCandidate) => Promise<boolean>;
}

function PickerBody({ conversationId, busy, onTransfer, onClose }: Omit<Props, "open" | "phone">) {
  const [list, setList] = useState<TransferCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    adminChat
      .staff(conversationId)
      .then((rows) => alive && setList(rows))
      .catch((e: unknown) => alive && setError(chatErrorText(e, "Не удалось загрузить сотрудников")));
    return () => {
      alive = false;
    };
  }, [conversationId, tick]);

  if (error) return <ListError message={error} onRetry={() => setTick((n) => n + 1)} />;
  if (!list) return <div className="flex flex-col gap-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>;
  if (list.length === 0) return <p className="text-sm text-on-surface-variant">{CHAT_TEXT.transferEmpty}</p>;

  const groups = transferGroups(list, query);
  const target = list.find((c) => c.id === chosen) ?? null;
  const section = (title: string, rows: TransferCandidate[]) =>
    rows.length > 0 ? (
      <div className="flex flex-col gap-1">
        <p className="px-1 text-xs font-medium text-on-surface-variant">{title}</p>
        {rows.map((c) => (
          <TransferRow key={c.id} candidate={c} chosen={c.id === chosen} onChoose={() => setChosen(c.id)} />
        ))}
      </div>
    ) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {list.length >= TRANSFER_SEARCH_FROM ? (
        <SearchInput value={query} onChange={setQuery} placeholder={CHAT_TEXT.transferSearch} ariaLabel={CHAT_TEXT.transferSearch} />
      ) : null}
      <div role="radiogroup" aria-label={CHAT_TEXT.transferTitle} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {section(CHAT_TEXT.transferLeadsOrder, groups.leadsOrder)}
        {section(groups.leadsOrder.length > 0 ? CHAT_TEXT.transferOthers : "", groups.others)}
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" className="min-h-12 sm:min-h-10" onClick={onClose}>
          {CHAT_TEXT.cancel}
        </Button>
        <Button
          className="min-h-12 w-full sm:min-h-10 sm:w-auto"
          disabled={!target}
          loading={busy}
          onClick={() => target && void onTransfer(target).then((ok) => ok && onClose())}
        >
          {CHAT_TEXT.transferSubmit}
        </Button>
      </div>
    </div>
  );
}

export function TransferPicker(props: Props) {
  const { open, phone, onClose } = props;
  const body = <PickerBody conversationId={props.conversationId} busy={props.busy} onTransfer={props.onTransfer} onClose={onClose} />;
  if (phone) {
    return (
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent side="bottom" className="max-h-[90dvh] gap-3 rounded-t-2xl p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <SheetHeader>
            <SheetTitle>{CHAT_TEXT.transferTitle}</SheetTitle>
          </SheetHeader>
          {open ? body : null}
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[80dvh] sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{CHAT_TEXT.transferTitle}</DialogTitle>
        </DialogHeader>
        {open ? body : null}
      </DialogContent>
    </Dialog>
  );
}
